import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

const useAutoSave = (postId, saveFunction, data, delay = 30000) => {
  const [lastSaved, setLastSaved] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  const timeoutRef = useRef(null);
  const lastDataRef = useRef(null);
  const initialDataRef = useRef(null);

  // Salvar dados iniciais para comparação
  useEffect(() => {
    if (!initialDataRef.current) {
      initialDataRef.current = JSON.stringify(data);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-save quando os dados mudam
  useEffect(() => {
    if (!postId || !saveFunction) return;

    const currentData = JSON.stringify(data);
    
    // Não salvar se os dados não mudaram ou são iguais aos iniciais
    if (currentData === lastDataRef.current || currentData === initialDataRef.current) {
      return;
    }

    // Limpar timeout anterior
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Configurar novo timeout
    timeoutRef.current = setTimeout(async () => {
      try {
        setSaving(true);
        setError(null);
        
        await saveFunction(postId, data);
        
        setLastSaved(new Date());
        lastDataRef.current = currentData;
        
        // Notificação discreta de autosave bem-sucedido
        toast.info('Rascunho salvo automaticamente', {
          position: "bottom-right",
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          className: 'autosave-toast',
        });
      } catch (error) {
        console.error('Erro no auto-save:', error);
        setError('Erro ao salvar automaticamente');
      } finally {
        setSaving(false);
      }
    }, delay);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [postId, data, saveFunction, delay]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Força um save imediato
  const forceSave = async () => {
    if (!postId || !saveFunction || saving) return;

    const currentData = JSON.stringify(data);
    
    if (currentData === lastDataRef.current) {
      return; // Nada para salvar
    }

    try {
      setSaving(true);
      setError(null);
      
      // Limpar timeout pendente
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      await saveFunction(postId, data);
      
      setLastSaved(new Date());
      lastDataRef.current = currentData;
      
      // Notificação de save manual bem-sucedido
      toast.success('Rascunho salvo com sucesso', {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (error) {
      console.error('Erro no save forçado:', error);
      setError('Erro ao salvar');
      throw error;
    } finally {
      setSaving(false);
    }
  };

  return {
    lastSaved,
    saving,
    error,
    forceSave
  };
};

export default useAutoSave;